import { getDashboardCounts, getTaskTimeSeries } from '@/apis/apis';
import useDocumentTitle from '@/components/Helmet'
import { useToast } from '@/hooks/use-toast';
import { Box, Card, Chip, Grid, Menu, MenuItem, Paper, Select, Typography } from '@mui/material';
import { Home, NotepadText, User } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts';

const UNIT_LABELS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];



const Dashboard = () => {
  useDocumentTitle("Dashboard");
  const { toast } = useToast()
  const [counts, setCounts] = useState({
    totalProperties: 0,
    vendors: 0,
    tasks: 0
  });
  const [timeSeries, setTimeSeries] = useState([]);
  const [range, setRange] = useState({ start: 0, end: 0 });
  const [unit, setUnit] = useState('day');

  const getCounts = async () => {
    try {
      const data = await getDashboardCounts();
      setCounts(data);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: "Something went wrong",
      })
    }
  }
  useEffect(() => {
    getCounts();
  }, [])

  const taskTimeSeries = async () => {
    try {
      const data = await getTaskTimeSeries({unit});
      const dataPoints = data.buckets.map((b: any) => ({
        time: new Date(b.ts).getTime(),
        cancelled: b.cancelled,
        completed: b.completed,
        in_progress: b.in_progress,
        scheduled: b.scheduled,
        total: b.total,
      }));
      setTimeSeries(dataPoints);
      setRange({ start: data.start, end: data.end });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: "Something went wrong",
      })
    }
  }

  useEffect(() => {
    taskTimeSeries();
  }, [unit])

  const options: any = useMemo(() => ({
    chart: { type: 'line', height: 320, animations: { enabled: false } },
    stroke: { curve: 'smooth' },
    xaxis: {
      type: 'datetime',
      tickAmount: unit === 'day' ? 6 : unit === 'year' ? 12 : 7,
      labels: {
        datetimeUTC: false, // let it display in local tz
      },
    },

    yaxis: { decimalsInFloat: 0 },
    tooltip: { x: { format: unit === 'day' ? 'HH:mm' : unit === 'year' ? 'MMM yyyy' : 'dd MMM' } },
    legend: { show: true },
  }), [unit, range]);

  const apexSeries = [
    { name: 'Total Task', data: timeSeries.map((p: any) => [p.time, p.total]) },
    { name: 'Completed', data: timeSeries.map((p: any) => [p.time, p.completed]) },
    { name: 'Scheduled', data: timeSeries.map((p: any) => [p.time, p.scheduled]) },
    { name: 'In Progress', data: timeSeries.map((p: any) => [p.time, p.in_progress]) },
    { name: 'Cancelled', data: timeSeries.map((p: any) => [p.time, p.cancelled]) },
  ];

  function FilterTabs({ unit, onChange }: any) {
    return (
      <div className='p-2 w-32'>
        <Select value={unit} onChange={(e) => onChange(e.target.value)} fullWidth size='small'>
          <MenuItem disabled value="">Select Filter</MenuItem>
          {UNIT_LABELS.map((u) => (
            <MenuItem
              key={u.key}
              value={u.key}
            >
              {u.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  }
  return (
    <div>
      <Grid container spacing={2}>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Card sx={{ p: 2, borderRadius : 4, background: 'linear-gradient(to right, #00db70, #0075b0)' }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Home size={40} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", gap: 2 }}>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>Property</Typography>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>
                <Chip
                  label={counts.totalProperties}
                  color="primary"
                  size="small"
                />
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Card sx={{ p: 2, borderRadius : 4, background: 'linear-gradient(90deg, hsla(192, 80%, 51%, 1) 0%, hsla(355, 85%, 63%, 1) 100%)' }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <User size={40} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", gap: 2 }}>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>Vendors</Typography>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>
                <Chip
                  label={counts.vendors}
                  color="primary"
                  size="small"
                />
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Card sx={{ p: 2, borderRadius : 4, background: 'linear-gradient(to right, #00dbab, #00b01f)' }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <NotepadText size={40} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", gap: 2 }}>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>Tasks</Typography>
              <Typography textAlign={"center"} variant="h5" fontWeight={600}>
                <Chip
                  label={counts.tasks}
                  color="primary"
                  size="small"
                />
              </Typography>
            </Box>
          </Card>

        </Grid>
        <Grid size={{ sm: 12, xs: 12 }}>
          <Paper style={{ minHeight: 320 }} elevation={0}>
            <div className='flex justify-between'>
              <Typography variant="h6" className="p-4 font-semibold">Tasks</Typography>
              <FilterTabs unit={unit} onChange={setUnit} />
            </div>
            <ReactApexChart options={options} series={apexSeries} type="line" height={320} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}

export default Dashboard